import { useLayoutEffect, useRef, useState } from "react";

import type { Racecard } from "@/api";
import { trackTitle } from "@/features/racing/venue";

import type { RacePlayback } from "./RaceRenderer";
import {
  formatRaceTime,
  rankByDistance,
  resolveDistanceWindow,
} from "./twoDBroadcast";

type RaceSample = {
  s: number;
  lane: number;
  v: number;
  u: number;
  finished: boolean;
};

const GATE_COLORS = [
  "#ef4444",
  "#f97316",
  "#eab308",
  "#22c55e",
  "#14b8a6",
  "#3b82f6",
  "#8b5cf6",
  "#ec4899",
  "#ef4444",
  "#f97316",
  "#eab308",
  "#22c55e",
];

const STAGE_BACKDROPS: Record<string, [string, string]> = {
  canyon: ["#1a1110", "#070708"],
  rose: ["#19131c", "#070708"],
  meridian: ["#0c1820", "#04090d"],
  tundra: ["#14212a", "#05090d"],
  skyfall: ["#10182a", "#05070d"],
  neon: ["#111029", "#05040b"],
};

export function TwoDRace({
  active,
  card,
  playback,
  getElapsedSeconds,
  replay,
  fullscreen,
  onReady,
  onError,
}: {
  active: boolean;
  card: Racecard;
  playback: RacePlayback | null;
  getElapsedSeconds: () => number;
  replay: boolean;
  fullscreen: boolean;
  onReady?: () => void;
  onError?: (message: string) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const playbackRef = useRef(playback);
  const callbacksRef = useRef({ onReady, onError });
  const [error, setError] = useState("");
  const playbackKey = playback
    ? `${playback.raceId}:${playback.startedAt}`
    : null;

  useLayoutEffect(() => {
    playbackRef.current = playback;
    callbacksRef.current = { onReady, onError };
  }, [onReady, onError, playback]);

  useLayoutEffect(() => {
    if (!active || !canvasRef.current) return;
    const canvas = canvasRef.current;
    let frame = 0;
    let cancelled = false;
    let observer: ResizeObserver | null = null;
    let resize: (() => void) | null = null;

    const fail = (caught: unknown) => {
      if (cancelled) return;
      const message =
        caught instanceof Error
          ? caught.message
          : "Verified 2D broadcast failed";
      setError(message);
      callbacksRef.current.onError?.(message);
    };

    async function start() {
      try {
        // Let the owning renderer commit its callbacks and reset the clock.
        await Promise.resolve();
        if (cancelled) return;
        const context = canvas.getContext("2d");
        if (!context) throw new Error("Canvas 2D context unavailable");
        const [{ planRace, raceSample }, { selectDisplayPixelRatio }] =
          await Promise.all([
            import("@renderer/race-physics.js"),
            import("@renderer/display-density.js"),
          ]);
        if (cancelled) return;
        const activePlayback = playbackRef.current;
        const plan = activePlayback ? planRace(activePlayback.physics) : null;
        const reducedMotion = window.matchMedia(
          "(prefers-reduced-motion: reduce)",
        ).matches;

        resize = () => {
          const rect = canvas.getBoundingClientRect();
          const pixelRatio = selectDisplayPixelRatio({
            cssWidth: rect.width,
            cssHeight: rect.height,
            devicePixelRatio: window.devicePixelRatio || 1,
            mobileTier: window.matchMedia("(max-width: 720px)").matches,
          });
          canvas.width = Math.max(1, Math.round(rect.width * pixelRatio));
          canvas.height = Math.max(1, Math.round(rect.height * pixelRatio));
          context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
        };
        if (typeof ResizeObserver !== "undefined") {
          observer = new ResizeObserver(resize);
          observer.observe(canvas);
        } else {
          window.addEventListener("resize", resize);
        }
        resize();

        let painted = false;
        const paint = () => {
          if (cancelled) return;
          try {
            const width = canvas.clientWidth;
            const height = canvas.clientHeight;
            if (width > 1 && height > 1) {
              const elapsedSeconds = getElapsedSeconds();
              const samples = plan
                ? (raceSample(plan, elapsedSeconds) as RaceSample[])
                : card.runners.map((_, gate) => ({
                    s: 0,
                    lane: gate,
                    v: 0,
                    u: 0,
                    finished: false,
                  }));
              drawBroadcast({
                context,
                width,
                height,
                card,
                samples,
                elapsedSeconds,
                hasPlayback: Boolean(activePlayback),
                minimalChrome: fullscreen,
                reducedMotion,
              });
              if (!painted) {
                painted = true;
                callbacksRef.current.onReady?.();
              }
            }
            frame = window.requestAnimationFrame(paint);
          } catch (caught) {
            fail(caught);
          }
        };
        setError("");
        frame = window.requestAnimationFrame(paint);
      } catch (caught) {
        fail(caught);
      }
    }

    void start();
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(frame);
      observer?.disconnect();
      if (!observer && resize) window.removeEventListener("resize", resize);
    };
  }, [active, card, fullscreen, getElapsedSeconds, playbackKey]);

  const state = error
    ? "error"
    : !playback
      ? "gates"
      : replay
        ? "replay"
        : "live";
  return (
    <div
      aria-hidden={!active}
      className={`race-renderer race-renderer--2d${active ? "" : " is-hidden"}`}
      data-broadcast-state={state}
      data-minimal-chrome={fullscreen || undefined}
    >
      <canvas aria-hidden ref={canvasRef} />
      <div className="race-2d__status">
        <strong>
          <i aria-hidden />
          {state === "error"
            ? "Unavailable"
            : state === "live"
              ? "Live"
              : state === "replay"
                ? "Replay"
                : "At the gates"}
        </strong>
        {!error ? <span>Locally verified</span> : null}
      </div>
      <span className="race-2d__sr-status" role="status">
        {state === "error"
          ? "Verified 2D broadcast is unavailable."
          : state === "live"
            ? "Live race broadcast joined at the current authoritative position."
            : state === "replay"
              ? "Historical race replaying from the start."
              : "Runners are at the gates."}
      </span>
      {error ? (
        <div className="race-renderer__error">
          2D broadcast unavailable: {error}
        </div>
      ) : null}
    </div>
  );
}

function drawBroadcast({
  context,
  width,
  height,
  card,
  samples,
  elapsedSeconds,
  hasPlayback,
  minimalChrome,
  reducedMotion,
}: {
  context: CanvasRenderingContext2D;
  width: number;
  height: number;
  card: Racecard;
  samples: RaceSample[];
  elapsedSeconds: number;
  hasPlayback: boolean;
  minimalChrome: boolean;
  reducedMotion: boolean;
}) {
  if (width <= 1 || height <= 1) return;
  const compact = width < 640;
  const tiny = width < 420;
  const visualScale = compact ? 1 : Math.max(1, Math.min(1.7, width / 1_500));
  const backdrop = STAGE_BACKDROPS[card.stage] ?? ["#101a24", "#04080c"];
  const background = context.createLinearGradient(0, 0, width, height);
  background.addColorStop(0, backdrop[0]);
  background.addColorStop(1, backdrop[1]);
  context.clearRect(0, 0, width, height);
  context.fillStyle = background;
  context.fillRect(0, 0, width, height);

  const top = (compact ? 64 : 76) * visualScale;
  const bottom = (compact ? 34 : 44) * visualScale;
  const left = (tiny ? 42 : compact ? 54 : 76) * visualScale;
  const right = (compact ? 36 : 54) * visualScale;
  const plotWidth = Math.max(80, width - left - right);
  const plotHeight = Math.max(80, height - top - bottom);
  const positions = card.runners.map((_, gate) =>
    Math.max(0, Number(samples[gate]?.s ?? 0)),
  );
  const distanceWindow = resolveDistanceWindow(
    positions,
    card.distanceM,
    compact,
  );
  const order = rankByDistance(positions);
  const rankByGate = new Map(order.map((gate, rank) => [gate, rank + 1]));
  const leaderGate = order[0] ?? 0;
  const markerStep =
    distanceWindow.end - distanceWindow.start <= 320 ? 50 : 100;
  const laneGap = (compact ? 3 : 5) * visualScale;
  const laneHeight =
    (plotHeight - laneGap * Math.max(0, card.runners.length - 1)) /
    Math.max(1, card.runners.length);

  if (!minimalChrome) {
    drawHeader({
      context,
      width,
      card,
      leaderName: card.runners[leaderGate]?.name ?? "",
      leaderPosition: distanceWindow.leader,
      elapsedSeconds,
      hasPlayback,
      compact,
      visualScale,
    });
  }
  drawDistanceScale({
    context,
    top,
    left,
    plotWidth,
    markerStep,
    start: distanceWindow.start,
    end: distanceWindow.end,
    compact,
    visualScale,
  });

  for (let gate = 0; gate < card.runners.length; gate += 1) {
    const y = top + gate * (laneHeight + laneGap);
    context.fillStyle =
      gate % 2 === 0 ? "rgba(255,255,255,0.105)" : "rgba(255,255,255,0.065)";
    roundRect(context, left, y, plotWidth, laneHeight, compact ? 4 : 6);
    context.fill();
    context.strokeStyle = "rgba(255,255,255,0.055)";
    context.lineWidth = 1;
    context.stroke();
    drawGate(context, gate, left, y + laneHeight / 2, compact, visualScale);
  }

  for (
    let marker = Math.ceil(distanceWindow.start / markerStep) * markerStep;
    marker <= distanceWindow.end;
    marker += markerStep
  ) {
    const x = distanceX(
      marker,
      distanceWindow.start,
      distanceWindow.end,
      left,
      plotWidth,
    );
    context.beginPath();
    context.strokeStyle =
      marker % (markerStep * 2) === 0
        ? "rgba(255,255,255,0.11)"
        : "rgba(255,255,255,0.055)";
    context.lineWidth = 1;
    context.moveTo(x, top);
    context.lineTo(x, top + plotHeight);
    context.stroke();
  }

  if (
    card.distanceM >= distanceWindow.start &&
    card.distanceM <= distanceWindow.end
  ) {
    drawFinishLine({
      context,
      x: distanceX(
        card.distanceM,
        distanceWindow.start,
        distanceWindow.end,
        left,
        plotWidth,
      ),
      top,
      plotHeight,
      compact,
      visualScale,
    });
  }

  if (
    hasPlayback &&
    distanceWindow.leader > 20 &&
    distanceWindow.leader < card.distanceM
  ) {
    const leaderX = distanceX(
      distanceWindow.leader,
      distanceWindow.start,
      distanceWindow.end,
      left,
      plotWidth,
    );
    context.beginPath();
    context.strokeStyle = "rgba(255,255,255,0.42)";
    context.lineWidth = 1;
    context.moveTo(leaderX, top - 18 * visualScale);
    context.lineTo(leaderX, top + plotHeight);
    context.stroke();
    context.fillStyle = "rgba(255,255,255,0.72)";
    context.font = `700 ${(compact ? 8 : 10) * visualScale}px sans-serif`;
    context.textAlign = "center";
    context.fillText("LEADER", leaderX, top - 25 * visualScale);
  }

  for (let gate = 0; gate < card.runners.length; gate += 1) {
    const runner = card.runners[gate];
    const sample = samples[gate];
    const y =
      top + gate * (laneHeight + laneGap) + Math.max(12, laneHeight / 2);
    const x = distanceX(
      Math.max(
        distanceWindow.start,
        Math.min(distanceWindow.end, positions[gate]),
      ),
      distanceWindow.start,
      distanceWindow.end,
      left,
      plotWidth,
    );
    const color = runner.silk || GATE_COLORS[gate % GATE_COLORS.length];
    drawRunnerTrail({
      context,
      x,
      y,
      left,
      laneHeight,
      color,
      compact,
      visualScale,
    });
    drawRunnerName({
      context,
      x,
      y,
      left,
      plotWidth,
      name: runner.name,
      color,
      compact,
      tiny,
      visualScale,
    });
    drawHorse({
      context,
      x,
      y,
      color,
      gate,
      elapsedSeconds,
      speed: Number(sample?.v ?? 0),
      compact,
      reducedMotion,
      visualScale,
    });
    drawRank({
      context,
      rank: rankByGate.get(gate) ?? gate + 1,
      x: left + plotWidth + (compact ? 18 : 27),
      y,
      compact,
      visualScale,
    });
  }
}

function drawHeader({
  context,
  width,
  card,
  leaderName,
  leaderPosition,
  elapsedSeconds,
  hasPlayback,
  compact,
  visualScale,
}: {
  context: CanvasRenderingContext2D;
  width: number;
  card: Racecard;
  leaderName: string;
  leaderPosition: number;
  elapsedSeconds: number;
  hasPlayback: boolean;
  compact: boolean;
  visualScale: number;
}) {
  const side = (compact ? 12 : 18) * visualScale;
  context.fillStyle = "rgba(255,255,255,0.52)";
  context.font = `600 ${(compact ? 9 : 11) * visualScale}px sans-serif`;
  context.textAlign = "left";
  context.fillText(
    `${trackTitle(card).toUpperCase()} · ${card.distanceM}M · ${card.runners.length} RUNNERS`,
    side,
    (compact ? 40 : 47) * visualScale,
  );

  context.textAlign = "right";
  context.fillStyle = "rgba(255,255,255,0.92)";
  context.font = `700 ${(compact ? 10 : 12) * visualScale}px ui-monospace, monospace`;
  context.fillText(
    hasPlayback
      ? `${formatRaceTime(elapsedSeconds)} · ${Math.round(leaderPosition)}m`
      : "WAITING FOR GATES",
    width - side,
    (compact ? 24 : 28) * visualScale,
  );
  if (!compact && hasPlayback) {
    context.fillStyle = "rgba(255,255,255,0.52)";
    context.font = `600 ${10 * visualScale}px sans-serif`;
    context.fillText(`Leader · ${leaderName}`, width - side, 47 * visualScale);
  }
}

function drawDistanceScale({
  context,
  top,
  left,
  plotWidth,
  markerStep,
  start,
  end,
  compact,
  visualScale,
}: {
  context: CanvasRenderingContext2D;
  top: number;
  left: number;
  plotWidth: number;
  markerStep: number;
  start: number;
  end: number;
  compact: boolean;
  visualScale: number;
}) {
  context.fillStyle = "rgba(255,255,255,0.7)";
  context.font = `700 ${(compact ? 8 : 10) * visualScale}px ui-monospace, monospace`;
  context.textAlign = "center";
  for (
    let marker = Math.ceil(start / markerStep) * markerStep;
    marker <= end;
    marker += markerStep
  ) {
    context.fillText(
      `${marker}m`,
      distanceX(marker, start, end, left, plotWidth),
      top - 9 * visualScale,
    );
  }
}

function drawGate(
  context: CanvasRenderingContext2D,
  gate: number,
  left: number,
  y: number,
  compact: boolean,
  visualScale: number,
) {
  const size = (compact ? 22 : 28) * visualScale;
  const x = left - size - (compact ? 7 : 12) * visualScale;
  context.fillStyle = GATE_COLORS[gate % GATE_COLORS.length];
  roundRect(context, x, y - size / 2, size, size, compact ? 5 : 7);
  context.fill();
  context.fillStyle = "#fff";
  context.font = `800 ${(compact ? 9 : 11) * visualScale}px sans-serif`;
  context.textAlign = "center";
  context.textBaseline = "middle";
  context.fillText(String(gate + 1), x + size / 2, y);
  context.textBaseline = "alphabetic";
}

function drawRunnerTrail({
  context,
  x,
  y,
  left,
  laneHeight,
  color,
  compact,
  visualScale,
}: {
  context: CanvasRenderingContext2D;
  x: number;
  y: number;
  left: number;
  laneHeight: number;
  color: string;
  compact: boolean;
  visualScale: number;
}) {
  const length = (compact ? 64 : 110) * visualScale;
  const start = Math.max(left, x - length);
  const height = Math.max(
    16 * visualScale,
    Math.min(laneHeight - 6 * visualScale, (compact ? 24 : 34) * visualScale),
  );
  const gradient = context.createLinearGradient(start, 0, x + 18, 0);
  gradient.addColorStop(0, "rgba(255,255,255,0)");
  gradient.addColorStop(0.38, withAlpha(color, 0.08));
  gradient.addColorStop(1, withAlpha(color, 0.34));
  context.fillStyle = gradient;
  context.beginPath();
  context.moveTo(start, y - height / 2);
  context.lineTo(x + 16 * visualScale, y - height / 2);
  context.lineTo(x + 26 * visualScale, y);
  context.lineTo(x + 16 * visualScale, y + height / 2);
  context.lineTo(start, y + height / 2);
  context.closePath();
  context.fill();
}

function drawRunnerName({
  context,
  x,
  y,
  left,
  plotWidth,
  name,
  color,
  compact,
  tiny,
  visualScale,
}: {
  context: CanvasRenderingContext2D;
  x: number;
  y: number;
  left: number;
  plotWidth: number;
  name: string;
  color: string;
  compact: boolean;
  tiny: boolean;
  visualScale: number;
}) {
  const fontSize = (tiny ? 8 : compact ? 9 : 11) * visualScale;
  context.font = `700 ${fontSize}px sans-serif`;
  const availableLeft = x - left;
  const measured = context.measureText(name).width;
  const placeLeft =
    availableLeft > measured + (compact ? 38 : 58) * visualScale;
  context.textAlign = placeLeft ? "right" : "left";
  context.fillStyle = "#f7fbff";
  context.shadowColor = "rgba(0,0,0,0.9)";
  context.shadowBlur = 4;
  context.fillText(
    name,
    placeLeft
      ? x - (compact ? 24 : 34) * visualScale
      : Math.min(
          left + plotWidth - measured,
          x + (compact ? 24 : 35) * visualScale,
        ),
    y + fontSize * 0.35,
  );
  context.shadowBlur = 0;
  context.strokeStyle = withAlpha(color, 0.8);
}

function drawHorse({
  context,
  x,
  y,
  color,
  gate,
  elapsedSeconds,
  speed,
  compact,
  reducedMotion,
  visualScale,
}: {
  context: CanvasRenderingContext2D;
  x: number;
  y: number;
  color: string;
  gate: number;
  elapsedSeconds: number;
  speed: number;
  compact: boolean;
  reducedMotion: boolean;
  visualScale: number;
}) {
  const scale = (compact ? 0.58 : 0.78) * visualScale;
  const stride = reducedMotion
    ? 0
    : Math.sin(elapsedSeconds * Math.max(8, speed * 0.7) + gate * 0.8);
  context.save();
  context.translate(x, y);
  context.scale(scale, scale);
  context.lineCap = "round";
  context.lineJoin = "round";

  context.strokeStyle = withAlpha(color, 0.9);
  context.lineWidth = 4;
  context.beginPath();
  context.moveTo(-23, -2);
  context.quadraticCurveTo(-35, -12 - stride * 2, -40, -4);
  context.stroke();

  context.fillStyle = color;
  context.strokeStyle = "rgba(244,250,255,0.86)";
  context.lineWidth = 1.4;
  context.beginPath();
  context.ellipse(-7, -2, 22, 10, -0.05, 0, Math.PI * 2);
  context.fill();
  context.stroke();

  context.beginPath();
  context.moveTo(7, -8);
  context.quadraticCurveTo(13, -22, 21, -26);
  context.lineTo(28, -20);
  context.quadraticCurveTo(21, -12, 19, -3);
  context.closePath();
  context.fill();
  context.stroke();
  context.beginPath();
  context.ellipse(28, -21, 8, 5, 0.16, 0, Math.PI * 2);
  context.fill();
  context.stroke();
  context.beginPath();
  context.moveTo(25, -26);
  context.lineTo(26, -33);
  context.lineTo(30, -26);
  context.fill();

  context.strokeStyle = "rgba(244,250,255,0.9)";
  context.lineWidth = 2.8;
  const front = stride * 6;
  const back = -stride * 6;
  drawLeg(context, 9, 4, front, 18);
  drawLeg(context, 3, 5, back, 19);
  drawLeg(context, -14, 4, back, 18);
  drawLeg(context, -19, 3, front, 17);

  context.fillStyle = "rgba(4,8,12,0.72)";
  roundRect(context, -8, -9, 12, 11, 2);
  context.fill();
  context.fillStyle = "#fff";
  context.font = "800 8px sans-serif";
  context.textAlign = "center";
  context.textBaseline = "middle";
  context.fillText(String(gate + 1), -2, -3);
  context.restore();
}

function drawLeg(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  stride: number,
  length: number,
) {
  context.beginPath();
  context.moveTo(x, y);
  context.lineTo(x + stride * 0.55, y + length * 0.55);
  context.lineTo(x + stride, y + length);
  context.stroke();
}

function drawRank({
  context,
  rank,
  x,
  y,
  compact,
  visualScale,
}: {
  context: CanvasRenderingContext2D;
  rank: number;
  x: number;
  y: number;
  compact: boolean;
  visualScale: number;
}) {
  const size = (compact ? 20 : 26) * visualScale;
  context.fillStyle =
    rank === 1
      ? "#f5c84c"
      : rank === 2
        ? "#c9d1da"
        : rank === 3
          ? "#b87947"
          : "rgba(255,255,255,0.08)";
  roundRect(context, x - size / 2, y - size / 2, size, size, size / 2);
  context.fill();
  context.fillStyle = rank <= 3 ? "#071018" : "rgba(255,255,255,0.76)";
  context.font = `800 ${(compact ? 9 : 11) * visualScale}px sans-serif`;
  context.textAlign = "center";
  context.textBaseline = "middle";
  context.fillText(String(rank), x, y);
  context.textBaseline = "alphabetic";
}

function drawFinishLine({
  context,
  x,
  top,
  plotHeight,
  compact,
  visualScale,
}: {
  context: CanvasRenderingContext2D;
  x: number;
  top: number;
  plotHeight: number;
  compact: boolean;
  visualScale: number;
}) {
  const cell = (compact ? 4 : 6) * visualScale;
  for (let row = 0; row < Math.ceil(plotHeight / cell); row += 1) {
    for (let column = 0; column < 2; column += 1) {
      context.fillStyle =
        (row + column) % 2 === 0 ? "#f6fbff" : "rgba(8,14,20,0.96)";
      context.fillRect(x + (column - 1) * cell, top + row * cell, cell, cell);
    }
  }
  context.fillStyle = "#f6c947";
  context.font = `800 ${(compact ? 8 : 10) * visualScale}px sans-serif`;
  context.textAlign = "center";
  context.fillText("FINISH", x, top - 25 * visualScale);
}

function distanceX(
  metres: number,
  start: number,
  end: number,
  left: number,
  plotWidth: number,
) {
  return left + ((metres - start) / Math.max(1, end - start)) * plotWidth;
}

function withAlpha(color: string, alpha: number) {
  if (/^#[0-9a-f]{6}$/i.test(color)) {
    const red = Number.parseInt(color.slice(1, 3), 16);
    const green = Number.parseInt(color.slice(3, 5), 16);
    const blue = Number.parseInt(color.slice(5, 7), 16);
    return `rgba(${red},${green},${blue},${alpha})`;
  }
  return `rgba(84,184,255,${alpha})`;
}

function roundRect(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
) {
  const bounded = Math.min(radius, width / 2, height / 2);
  context.beginPath();
  context.moveTo(x + bounded, y);
  context.arcTo(x + width, y, x + width, y + height, bounded);
  context.arcTo(x + width, y + height, x, y + height, bounded);
  context.arcTo(x, y + height, x, y, bounded);
  context.arcTo(x, y, x + width, y, bounded);
  context.closePath();
}
